import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Coins, Infinity, Gift } from 'lucide-react';
import api from '@/lib/api';

// Light-weight, refreshable credit counter that adapts to:
//  • Unlimited plans (Crew/Business): shows the infinity glyph
//  • Trialing subscribers: shows numeric total + a small "Trial" badge
//  • Credit-gated plans (Solo, top-ups): shows numeric total
//  • No subscription: shows "Start trial" CTA
export function CreditCounter() {
    const [credits, setCredits] = useState({ monthly: 0, topup: 0, total: 0, unlimited: false, active: false, subscription_status: 'inactive' });
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        try {
            const r = await api.get('/billing/balance');
            const data = r.data.credits || {};
            setCredits({
                monthly: data.monthly || 0,
                topup: data.topup || 0,
                total: data.total || 0,
                unlimited: !!data.unlimited,
                active: !!data.active,
                subscription_status: data.subscription_status || 'inactive',
            });
        } catch (_e) {
            // ignore
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
        const handler = () => load();
        window.addEventListener('qf:credits-refresh', handler);
        const interval = setInterval(load, 45000);
        return () => {
            window.removeEventListener('qf:credits-refresh', handler);
            clearInterval(interval);
        };
    }, [load]);

    if (loading) {
        return (
            <span className="inline-flex items-center gap-1.5 bg-slate-900 text-slate-50 rounded-full pl-3 pr-3 py-1.5 text-sm font-medium" data-testid="credit-balance-counter">
                <Coins className="h-4 w-4 text-amber-400" />
                <span>—</span>
            </span>
        );
    }

    // Unlimited (Crew/Business, active or trialing)
    if (credits.unlimited) {
        const isTrialing = credits.subscription_status === 'trialing';
        return (
            <Link
                to="/app/billing"
                data-testid="credit-balance-counter"
                className="inline-flex items-center gap-1.5 sm:gap-2 bg-amber-500 hover:bg-amber-400 text-slate-950 rounded-full pl-3 pr-3 sm:pr-4 py-1.5 text-sm font-semibold"
                title={isTrialing ? '7-day free trial · unlimited access' : 'Unlimited credits on your plan'}
            >
                <Infinity className="h-4 w-4" strokeWidth={2.5} />
                <span className="hidden sm:inline" data-testid="credit-balance-total">
                    {isTrialing ? 'Unlimited · Trial' : 'Unlimited'}
                </span>
                <span className="sm:hidden" data-testid="credit-balance-total">∞</span>
            </Link>
        );
    }

    // No active subscription at all → hard nudge to start trial.
    // Server-side paywall is the real gate; this is just clearer UX.
    if (!credits.active) {
        return (
            <Link
                to="/pricing"
                data-testid="credit-balance-counter"
                className="inline-flex items-center gap-1.5 bg-rose-50 hover:bg-rose-100 border border-rose-200 text-rose-700 rounded-full pl-3 pr-3 sm:pr-4 py-1.5 text-sm font-semibold"
                title="Start your 7-day free trial to unlock the app"
            >
                <Coins className="h-4 w-4" />
                <span data-testid="credit-balance-total">Start trial</span>
                <span className="hidden sm:inline text-xs font-medium ml-1 text-rose-600">→</span>
            </Link>
        );
    }

    // Trialing Solo user — show numeric lookups with a small "Trial" tag
    if (credits.subscription_status === 'trialing') {
        const lowBalance = credits.total <= 5;
        return (
            <Link
                to="/app/billing"
                data-testid="credit-balance-counter"
                className={`inline-flex items-center gap-1 sm:gap-1.5 ${lowBalance ? 'bg-amber-100 border-amber-300' : 'bg-amber-50 border-amber-200'} hover:bg-amber-100 border text-amber-900 rounded-full pl-2.5 pr-3 py-1 text-sm font-medium`}
                title="7-day free trial active"
            >
                <Gift className="h-3.5 w-3.5 text-amber-700" />
                <span className="hidden sm:inline text-[10px] uppercase tracking-wider text-amber-700 font-semibold mr-1">Trial</span>
                <span className="tabular-nums text-xs" data-testid="credit-balance-total">
                    <b>{credits.total}</b>
                    <span className="hidden sm:inline text-[10px] ml-0.5 text-amber-700/80">lookups</span>
                </span>
            </Link>
        );
    }

    // Numeric credit-gated (Solo, active subscription)
    const showWarn = credits.total < 5;
    return (
        <Link
            to="/app/billing"
            data-testid="credit-balance-counter"
            className={`inline-flex items-center gap-1.5 sm:gap-2 ${showWarn ? 'bg-rose-50 text-rose-700 border border-rose-200' : 'bg-slate-900 text-slate-50 hover:bg-slate-800'} rounded-full pl-3 pr-3 sm:pr-4 py-1.5 text-sm font-medium`}
            title={`${credits.monthly} monthly + ${credits.topup} top-up lookups`}
        >
            <Coins className={`h-4 w-4 ${showWarn ? 'text-rose-700' : 'text-amber-400'}`} />
            <span className="tabular-nums" data-testid="credit-balance-total">{credits.total}</span>
            <span className={`hidden sm:inline text-xs ml-1 ${showWarn ? 'text-rose-700' : 'text-slate-300'}`}>lookups</span>
        </Link>
    );
}

export function refreshCreditCounter() {
    window.dispatchEvent(new CustomEvent('qf:credits-refresh'));
}
